import { useState, type FormEvent } from 'react';
import {
  ListPage, Badge, AsyncBoundary, Drawer, Button, Notice, useToast, KpiCard,
} from '@kollez/design-system';
import { useCompliance, useCreateCompliance, useUpdateCompliance, type ComplianceRow } from '@/lib/queries';

const STATUS_TONE: Record<string, 'success' | 'warning' | 'info' | 'outline' | 'danger'> = {
  planned: 'outline',
  in_progress: 'info',
  submitted: 'info',
  accepted: 'success',
  rejected: 'warning',
  overdue: 'danger',
};

/**
 * /group/compliance (F62) — every regulator deadline the group has to hit. Rows show `daysToDue`
 * so the admin sees "AICTE Mandatory Disclosure due in 14 days" at a glance. Overdue → red.
 */
export function GroupCompliancePage() {
  const { data, isLoading, error, refetch } = useCompliance();
  const [openNew, setOpenNew] = useState(false);
  const [openUpdate, setOpenUpdate] = useState<ComplianceRow | null>(null);
  const rows = data?.submissions ?? [];

  const totals = {
    total: rows.length,
    overdue: rows.filter((r) => r.derivedStatus === 'overdue').length,
    upcoming30: rows.filter((r) => r.daysToDue >= 0 && r.daysToDue <= 30).length,
    submitted: rows.filter((r) => ['submitted', 'accepted'].includes(r.status)).length,
  };

  return (
    <div className="stack">
      {/* F-080: page header lives OUTSIDE the AsyncBoundary. Otherwise the empty state
          short-circuits and the page renders as a headless blank — the audit's blank-page
          finding on this route. Every list page follows this shape. */}
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Compliance</p>
          <h1 className="h-page">Regulator deadlines</h1>
          <p className="subhead">
            Kollez tracks; you file. Every filing carries a due date, an owner, and an evidence link once submitted.
          </p>
        </div>
        <div className="ph-actions">
          <Button variant="primary" onClick={() => setOpenNew(true)}>+ Add deadline</Button>
        </div>
      </div>
      <AsyncBoundary
        kind="table"
        loading={isLoading}
        error={error}
        empty={!isLoading && rows.length === 0}
        emptyTitle="No compliance deadlines yet"
        emptyHint="Add regulator filings you need to hit — AICTE Mandatory Disclosure, NAAC self-study, NBA program-wise, etc."
        emptyAction={<Button variant="primary" size="sm" onClick={() => setOpenNew(true)}>+ Add deadline</Button>}
        onRetry={() => void refetch()}
      >
      <div className="stack">

        {totals.overdue > 0 && (
          <Notice tone="warning">
            <b>{totals.overdue} deadline{totals.overdue === 1 ? '' : 's'} overdue.</b> Regulators don't accept late filings without penalty — file soon.
          </Notice>
        )}

        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <KpiCard label="Total deadlines" value={totals.total} />
          <KpiCard label="Overdue" value={totals.overdue} tone={totals.overdue ? 'danger' : 'success'} />
          <KpiCard label="Upcoming (30d)" value={totals.upcoming30} tone="warning" />
          <KpiCard label="Submitted / accepted" value={totals.submitted} tone="success" />
        </div>

        <ListPage<ComplianceRow>
          eyebrow="All filings"
          title="Deadlines"
          segment={{
            key: 'derivedStatus',
            options: [
              ['all', 'All'],
              ['overdue', 'Overdue'],
              ['planned', 'Planned'],
              ['in_progress', 'In progress'],
              ['submitted', 'Submitted'],
              ['accepted', 'Accepted'],
            ],
          }}
          filters={[
            { key: 'regulator', label: 'Regulator', options: ['AICTE', 'NAAC', 'NBA', 'NIRF', 'UGC', 'DTE'] },
          ]}
          rows={rows}
          rowKey={(r) => r._id}
          columns={[
            {
              key: 'r',
              label: 'Filing',
              render: (r) => (
                <div>
                  <div className="cell-title">{r.name}</div>
                  <div className="cell-sub">
                    <Badge tone="outline">{r.regulator}</Badge>{r.cycle && <> · {r.cycle}</>}
                  </div>
                </div>
              ),
            },
            {
              key: 'due',
              label: 'Due',
              render: (r) => new Date(r.dueDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }),
            },
            {
              key: 'days',
              label: 'Days',
              right: true,
              render: (r) => (
                <span className={r.daysToDue < 0 ? 'text-ink-danger font-medium' : r.daysToDue <= 30 ? 'text-ink-warning' : ''}>
                  {r.daysToDue < 0 ? `${Math.abs(r.daysToDue)}d late` : `${r.daysToDue}d`}
                </span>
              ),
            },
            {
              key: 'status',
              label: 'Status',
              right: true,
              render: (r) => <Badge tone={STATUS_TONE[r.derivedStatus] ?? 'outline'}>{r.derivedStatus.replace(/_/g, ' ')}</Badge>,
            },
            {
              key: 'act',
              label: '',
              right: true,
              render: (r) => (
                <button className="link text-xs" type="button" onClick={(e) => { e.stopPropagation(); setOpenUpdate(r); }}>
                  Update →
                </button>
              ),
            },
          ]}
          drawer={(r) => ({
            eyebrow: r.regulator,
            title: r.name,
            body: (
              <div className="grid gap-3 text-sm">
                {r.description && <p className="text-ink-secondary whitespace-pre-line">{r.description}</p>}
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-ink-tertiary">Cycle</span><div><b>{r.cycle ?? '—'}</b></div></div>
                  <div><span className="text-ink-tertiary">Due date</span><div><b>{new Date(r.dueDate).toLocaleDateString('en-IN')}</b></div></div>
                  {r.submittedAt && <div><span className="text-ink-tertiary">Submitted</span><div><b>{new Date(r.submittedAt).toLocaleDateString('en-IN')}</b></div></div>}
                  {r.acceptedAt && <div><span className="text-ink-tertiary">Accepted</span><div><b>{new Date(r.acceptedAt).toLocaleDateString('en-IN')}</b></div></div>}
                </div>
                {r.notes && <div className="p-3 bg-bg-info/10 rounded text-xs whitespace-pre-line">{r.notes}</div>}
                {r.evidenceUrl && <a className="link text-sm" href={r.evidenceUrl} target="_blank" rel="noreferrer">Open evidence file ↗</a>}
                <div className="pt-2 border-t border-line-tertiary">
                  <Button size="sm" variant="primary" onClick={() => setOpenUpdate(r)}>Update status</Button>
                </div>
              </div>
            ),
          })}
        />
      </div>

      {openNew && <NewDeadlineDrawer onClose={() => setOpenNew(false)} />}
      {openUpdate && <UpdateStatusDrawer row={openUpdate} onClose={() => setOpenUpdate(null)} />}
      </AsyncBoundary>
    </div>
  );
}

function NewDeadlineDrawer({ onClose }: { onClose: () => void }) {
  const create = useCreateCompliance();
  const toast = useToast();
  const [regulator, setRegulator] = useState<'AICTE' | 'NAAC' | 'NBA' | 'NIRF' | 'UGC' | 'DTE' | 'Other'>('AICTE');
  const [name, setName] = useState('');
  const [cycle, setCycle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [desc, setDesc] = useState('');

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || !dueDate) {
      toast.show({ tone: 'warning', message: 'Name and due date are required.' });
      return;
    }
    try {
      await create.mutateAsync({ regulator, name, cycle: cycle || undefined, dueDate, description: desc || undefined });
      toast.show({ title: 'Deadline added' });
      onClose();
    } catch (err) {
      toast.show({
        tone: 'warning',
        message: (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Create failed',
      });
    }
  }

  return (
    <Drawer
      open
      onOpenChange={(o) => !o && onClose()}
      title="New compliance deadline"
      description="Add a regulator deadline you need to hit. Kollez reminds you; you file."
      size="md"
      footer={
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={create.isPending}>Save</Button>
        </div>
      }
    >
      <form onSubmit={submit} className="grid gap-4">
        <div className="grid grid-cols-2 gap-4">
          <label className="grid gap-1.5">
            <span className="label text-sm">Regulator</span>
            <select className="select" value={regulator} onChange={(e) => setRegulator(e.target.value as typeof regulator)}>
              <option value="AICTE">AICTE</option>
              <option value="NAAC">NAAC</option>
              <option value="NBA">NBA</option>
              <option value="NIRF">NIRF</option>
              <option value="UGC">UGC</option>
              <option value="DTE">DTE</option>
              <option value="Other">Other</option>
            </select>
          </label>
          <label className="grid gap-1.5">
            <span className="label text-sm">Cycle</span>
            <input className="input" value={cycle} onChange={(e) => setCycle(e.target.value)} placeholder="AY 2025-26" />
          </label>
        </div>
        <label className="grid gap-1.5">
          <span className="label text-sm">Filing name</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Mandatory Disclosure" />
        </label>
        <label className="grid gap-1.5">
          <span className="label text-sm">Due date</span>
          <input className="input" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </label>
        <label className="grid gap-1.5">
          <span className="label text-sm">Description (optional)</span>
          <textarea className="input" rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} />
        </label>
      </form>
    </Drawer>
  );
}

function UpdateStatusDrawer({ row, onClose }: { row: ComplianceRow; onClose: () => void }) {
  const update = useUpdateCompliance();
  const toast = useToast();
  const [status, setStatus] = useState<ComplianceRow['status']>(row.status === 'overdue' ? 'in_progress' : row.status);
  const [evidence, setEvidence] = useState(row.evidenceUrl ?? '');
  const [notes, setNotes] = useState(row.notes ?? '');

  async function submit() {
    try {
      await update.mutateAsync({
        id: row._id,
        status: status === 'overdue' ? 'in_progress' : status,
        evidenceUrl: evidence || undefined,
        notes: notes || undefined,
      });
      toast.show({ title: 'Status updated' });
      onClose();
    } catch (err) {
      toast.show({
        tone: 'warning',
        message: (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Update failed',
      });
    }
  }

  return (
    <Drawer
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Update: ${row.name}`}
      description={row.regulator + (row.cycle ? ` · ${row.cycle}` : '')}
      size="md"
      footer={
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={update.isPending}>Save</Button>
        </div>
      }
    >
      <div className="grid gap-4">
        <label className="grid gap-1.5">
          <span className="label text-sm">Status</span>
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value as ComplianceRow['status'])}>
            <option value="planned">Planned</option>
            <option value="in_progress">In progress</option>
            <option value="submitted">Submitted</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
          </select>
        </label>
        <label className="grid gap-1.5">
          <span className="label text-sm">Evidence URL (optional)</span>
          <input className="input" value={evidence} onChange={(e) => setEvidence(e.target.value)} placeholder="https://…" />
        </label>
        <label className="grid gap-1.5">
          <span className="label text-sm">Notes</span>
          <textarea className="input" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
      </div>
    </Drawer>
  );
}
